import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import Equipment, { EquipmentType } from "./Equipment";
import Gallery, { GalleryType } from "./Gallery";
import Highlights, { HighlightsType } from "./Highlights";
import Table, { TableType } from "./Table";
import { useHistory } from "react-router";
import { FacebookShareButton, WhatsappShareButton } from "react-share";
import style from "./index.module.css";
import { useTranslation } from "../../../../hooks/useTranslation";
import { VehicleConditions, VehicleTypesT } from "../../../../utils/GeneralTypes";

const VehicleSpecs = ({ specs, ctas }: ComponentPropsType) => {
  const { locale } = useTranslation();

  const [activePage, setActivePage] = useState<number>(
    specs.findIndex(
      (el) =>
        (el.table && el.table.length > 0) ||
        (el.equipment && el.equipment.length > 0) ||
        (el.gallery && el.gallery.length > 0) ||
        (el.highlights && el.highlights.length > 0) ||
        el.description,
    ) || 0,
  );

  const history = useHistory();

  const isEmptySpecs = useMemo(() => {
    let isEmpty = true;
    specs.forEach((spec) => {
      if (spec.gallery && spec.gallery.length > 0) isEmpty = false;
      if (spec.highlights && spec.highlights.length > 0) isEmpty = false;
      if (spec.equipment && spec.equipment.length > 0) isEmpty = false;
      if (spec.table && spec.table.length > 0) isEmpty = false;
      if (spec.description) isEmpty = false;
    });
    return isEmpty;
  }, [specs]);

  const onShowShareHandler = useCallback(() => {
    const share_menu = document.querySelector(`.${style.specs_share_menu}`);
    if (share_menu) {
      share_menu.classList.add(style.active);
    }
  }, []);

  const onHideShareHandler = useCallback((e: any) => {
    const share_menu = document.querySelector(`.${style.specs_share_menu}`);
    if (!e.target.closest(`.${style.specs_share_icon}`) && share_menu?.classList.contains(style.active))
      share_menu?.classList.remove(style.active);
  }, []);

  useEffect(() => {
    const share_menu = document.querySelector(`.${style.specs_share_menu}`);
    if (share_menu) {
      document.addEventListener("click", onHideShareHandler);
    }
    return () => {
      document.removeEventListener("click", onHideShareHandler);
    };
  }, [onHideShareHandler]);

  return (
    <>
      {!isEmptySpecs && (
        <>
          <div className={style.specs_header}>
            <ul className={style.specs_controllers}>
              {specs.map(
                (spec, index) =>
                  ((spec?.gallery && spec.gallery.length > 0) ||
                    (spec?.equipment && spec.equipment.length > 0) ||
                    (spec?.highlights && spec.highlights.length > 0) ||
                    spec?.description ||
                    (spec?.table && spec.table.length > 0)) && (
                    <li
                      key={index}
                      {...(activePage === index ? { className: style.active } : {})}
                      onClick={() => (activePage === index ? {} : setActivePage(index))}
                    >
                      {spec.title}
                    </li>
                  ),
              )}
            </ul>
            {ctas.length > 0 && (
              <div className={style.specs_ctas}>
                {ctas.map((cta, index) => (
                  <button
                    key={index}
                    {...(cta.share
                      ? {
                          onClick: onShowShareHandler,
                          className: style.specs_share_icon,
                        }
                      : {
                          onClick: () => (cta.link ? history.push(cta.link) : cta.cb()),
                        })}
                  >
                    {cta.icon}
                    {cta.label && <span>{cta.label}</span>}
                  </button>
                ))}
                {ctas.findIndex((cta) => cta.share) !== -1 && (
                  <ul className={style.specs_share_menu}>
                    <li>
                      <FacebookShareButton url={window.location.href}>
                        <span className={style.specs_share_menu__item}>{locale("facebook")}</span>
                      </FacebookShareButton>
                    </li>
                    <li>
                      <WhatsappShareButton url={window.location.href}>
                        <span className={style.specs_share_menu__item}>{locale("whatsapp")}</span>
                      </WhatsappShareButton>
                    </li>
                    <li>
                      <span
                        className={style.specs_share_menu__item}
                        onClick={() => navigator.clipboard.writeText(window.location.href)}
                      >
                        {locale("copy_link")}
                      </span>
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className={style.specs_details}>
            {specs.map(
              (spec, index) =>
                activePage === index && (
                  <React.Fragment key={index}>
                    {(spec.type === "gallery" && spec.gallery && (
                      <Gallery gallery={spec.gallery} galleryType={spec.gallery_type as "square" | "grid"} />
                    )) ||
                      (spec.type === "highlights" && spec.highlights && <Highlights highlights={spec.highlights} />) ||
                      (spec.type === "equipment" && spec.equipment && <Equipment equipment={spec.equipment} />) ||
                      (spec.type === "description" && spec.description && (
                        <p style={{ whiteSpace: "pre-wrap" }}>{spec.description}</p>
                      )) ||
                      (spec.type === "table" && spec.table && <Table table={spec.table} />)}
                  </React.Fragment>
                ),
            )}
          </div>
        </>
      )}
    </>
  );
};

export default VehicleSpecs;

export type SpecsType = {
  title: string;
  type: "gallery" | "highlights" | "equipment" | "description" | "table";
  gallery_type?: "grid" | "square";
  gallery?: GalleryType;
  highlights?: HighlightsType[];
  equipment?: EquipmentType[];
  description?: string;
  table?: TableType;
};

export type CtasType = {
  icon: ReactElement;
  cb: CallableFunction;
  label?: string;
  link?: string;
  share?: boolean;
  whatsapp?: boolean;
  phone?: boolean;
};

type ComponentPropsType = {
  specs: SpecsType[];
  ctas: CtasType[];
};
